import { useEffect, useState, useCallback } from 'react';
import { X, Loader2, CreditCard } from 'lucide-react';
import { billingApi, Invoice, BackendInvoiceStatus } from '../api/billingApi';
import { BrandingData, API_HOST } from '../api/brandingApi';
import { toast } from 'react-toastify';
import { formatCurrency } from '../utils/currency';

// Re-using the Item interface from InvoiceForm for consistency
interface Item {
  id: string;
  description: string;
  quantity: number;
  price: number;
}

interface PreviewProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId?: string; // Optional invoice ID for payment link
  branding?: BrandingData | null;
  data: {
    clientName: string;
    clientEmail: string;
    customerAddress: string;
    customerPhone: string;
    dueDate: string;
    notes: string;
    currencyCode?: string;
    items: Item[];
    totals: {
      subtotal: number;
      tax: number;
      total: number;
    };
  };
}

export default function InvoicePreview({ isOpen, onClose, invoiceId, branding, data }: PreviewProps) {
  // Extended type to include optional branding snapshots and legacy properties
  type InvoiceWithBranding = Invoice & { 
    branding?: BrandingData; 
    companyLogoUrl?: string;
    notes?: string | null;
  };
  const [invoice, setInvoice] = useState<InvoiceWithBranding | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billingApi.getInvoiceById(invoiceId!);
      setInvoice(res);
    } catch (err) {
      toast.error("Could not load invoice details");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [invoiceId, onClose]);

  useEffect(() => {
    if (isOpen && invoiceId) {
      fetchDetails();
    }
  }, [isOpen, invoiceId, fetchDetails]);

  // const handleDownload = async () => {
  //   if (!invoiceId) return;
  //   try {
  //     const blob = await billingApi.downloadPdf(invoiceId);
  //     const url = window.URL.createObjectURL(blob);
  //     const a = document.createElement('a');
  //     a.href = url;
  //     a.download = `invoice-${invoice?.invoiceNumber || 'file'}.pdf`;
  //     a.click();
  //   } catch (err) {
  //     toast.error("Failed to download");
  //   }
  // };

  const getFullUrl = (url?: string) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_HOST}${url}`;
  };

  if (!isOpen) return null;

  const displayData = invoice? (() => {
      return {
        clientName: invoice.customerName,
        clientEmail: invoice.customerEmail,
        customerAddress: invoice.customerAddress || '',
        customerPhone: invoice.customerPhone || '',
        dueDate: new Date(invoice.dueDate).toLocaleDateString(),
        notes: invoice.notes || '',
        items: invoice.items.map((it: any, i) => ({
          id: i.toString(),
          description: it.description,
          quantity: it.quantity,
          price: it.unitPrice
        })),
        totals: {
          subtotal: invoice.subtotal,
          tax: invoice.taxAmount,
          total: invoice.totalAmount
        },
        currencyCode: invoice.currencyCode || 'SGD',
      };
    })()
  : data;

  // Branding resolution logic based on priority
  const logoToDisplay = invoice?.branding?.logoUrl || invoice?.companyLogoUrl || branding?.logoUrl;
  const nameToDisplay = invoice?.branding?.companyName || branding?.companyName || 'Strivre Services';
  const addressToDisplay = invoice?.branding?.address || branding?.address;
  const phoneToDisplay = invoice?.branding?.contactNumber || branding?.contactNumber;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      
      {/* Modal Container */}
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-[2rem] shadow-2xl flex flex-col overflow-hidden animate-scale-in">
        {/* Header Actions */}
        <div className="px-8 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            Invoice Preview
          </h2>
          <div className="flex items-center gap-2">
            {/* <button className="p-2 hover:bg-white rounded-xl text-slate-500 hover:text-strivre-burgundy transition-colors border border-transparent hover:border-strivre-lightnavy" title="Print">
              <Printer size={18} />
            </button>
            <button onClick={handleDownload} className="p-2 hover:bg-white rounded-xl text-slate-500 hover:text-strivre-burgundy transition-colors border border-transparent hover:border-strivre-lightnavy" title="Download">
              <Download size={18} />
            </button> */}
            <button onClick={onClose} className="p-2 hover:bg-white rounded-xl text-slate-500 hover:text-red-600 transition-colors border border-transparent hover:border-strivre-lightnavy ml-2">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Preview Area */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-8 lg:p-12 bg-strivre-softgrey/30">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <Loader2 className="w-10 h-10 text-strivre-burgundy animate-spin" />
              <p className="text-slate-500 font-medium">Loading Invoice Data...</p>
            </div>
          ) : (
          <div className="bg-white shadow-sm border border-strivre-lightnavy rounded-2xl mx-auto w-full max-w-2xl min-h-[600px] md:min-h-[800px] p-4 sm:p-12 flex flex-col overflow-hidden">
            {/* Branding Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start gap-6 mb-12 text-left">
              <div className="space-y-4 w-full sm:w-auto">
                {logoToDisplay ? (
                  <div className="max-w-[200px] h-auto">
                    <img 
                      src={getFullUrl(logoToDisplay) || ''} 
                      alt={`${nameToDisplay} Logo`}
                      className="max-h-16 w-auto object-contain border border-slate-100 rounded-lg p-1 bg-white shadow-sm"
                    />
                  </div>
                ) : (
              <div className="w-14 h-14 bg-strivre-burgundy rounded-xl flex items-center justify-center font-bold text-white text-2xl uppercase shadow-md">
                    {nameToDisplay.charAt(0)}
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="font-bold text-slate-900 text-base sm:text-lg uppercase tracking-tight leading-none">
                    {nameToDisplay}
                  </h3>
                  {addressToDisplay && (
                    <p className="text-[10px] sm:text-xs text-slate-500 mt-2 leading-relaxed whitespace-pre-line">
                      {addressToDisplay}
                      {phoneToDisplay && <><br />{phoneToDisplay}</>}
                    </p>
                  )}
                </div>
              </div>
              <div className="sm:text-right space-y-1">
                <h1 className="text-2xl sm:text-3xl font-black text-slate-900 uppercase tracking-tighter leading-none">Invoice</h1>
                <p className="text-xs sm:text-sm font-bold text-strivre-burgundy">#{invoice?.invoiceNumber || 'INV-PREVIEW'}</p>
                <p className="text-[10px] sm:text-xs text-slate-400 font-medium">Due: {displayData.dueDate || 'Not set'}</p>
              </div>
            </div>

            {/* Bill To */}
            <div className="mb-12 text-left">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Bill To</p>
              <h4 className="font-bold text-slate-900">{displayData.clientName || 'Unnamed Client'}</h4>
              <p className="text-sm text-slate-500">{displayData.clientEmail || 'email@example.com'}</p>
              {displayData.customerAddress && (
                <p className="text-xs text-slate-400 mt-1 whitespace-pre-line">{displayData.customerAddress}</p>
              )}
              {displayData.customerPhone && (
                <p className="text-xs text-slate-400 mt-1">{displayData.customerPhone}</p>
              )}
            </div>

            {/* Items Table */}
            <div className="flex-1">
              {/* Desktop Table View */}
              <table className="hidden sm:table w-full text-left">
                <thead>
                  <tr className="border-b-2 border-slate-900">
                    <th className="py-3 text-[10px] font-bold text-slate-900 uppercase tracking-wider">Description</th>
                    <th className="py-3 text-[10px] font-bold text-slate-900 uppercase tracking-wider text-center">Qty</th>
                    <th className="py-3 text-[10px] font-bold text-slate-900 uppercase tracking-wider text-right">Price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {displayData.items.map((item) => (
                    <tr key={item.id}>
                      <td className="py-4 text-sm font-medium text-slate-700">{item.description || 'New Item'}</td>
                      <td className="py-4 text-sm text-slate-500 text-center">{item.quantity}</td>
                      <td className="py-4 text-sm font-bold text-slate-900 text-right">{formatCurrency(item.quantity * item.price, displayData.currencyCode)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile List View */}
              <div className="sm:hidden divide-y divide-slate-100 border-t-2 border-slate-900">
                {displayData.items.map((item) => (
                  <div key={item.id} className="py-4 space-y-1">
                    <p className="text-sm font-bold text-slate-900">{item.description || 'New Item'}</p>
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Qty: {item.quantity}</span>
                      <span className="font-bold text-slate-900">{formatCurrency(item.quantity * item.price, displayData.currencyCode)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals Section */}
            <div className="mt-12 border-t-2 border-slate-900 pt-6 flex flex-col items-end gap-2">
              <div className="flex justify-between w-full max-w-[200px] text-sm text-slate-500"><span>Subtotal</span><span>{formatCurrency(displayData.totals.subtotal, displayData.currencyCode)}</span></div>
              <div className="flex justify-between w-full max-w-[200px] text-sm text-slate-500"><span>Tax ({invoice?.branding?.taxAmount ?? 0}%)</span><span>{formatCurrency(displayData.totals.tax, displayData.currencyCode)}</span></div>
              <div className="flex justify-between w-full max-w-[200px] text-lg font-black text-slate-900 pt-2"><span>Total</span><span>{formatCurrency(displayData.totals.total, displayData.currencyCode)}</span></div>
              
              {invoice?.paidAmount !== undefined && invoice.paidAmount > 0 && (
                <>
                  <div className="flex justify-between w-full max-w-[200px] text-sm text-emerald-600 font-bold mt-2"><span>Paid</span><span>{formatCurrency(-invoice.paidAmount, displayData.currencyCode)}</span></div>
                  <div className="flex justify-between w-full max-w-[200px] text-base font-black text-red-600 pt-1 border-t border-red-100"><span>Balance</span><span>{formatCurrency(invoice.remainingBalance ?? 0, displayData.currencyCode)}</span></div>
                </>
              )}
            </div>

            {displayData.notes && (
              <div className="mt-12 pt-12 border-t border-slate-100"><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Notes</p><p className="text-xs text-slate-500 leading-relaxed">{displayData.notes}</p></div>
            )}

            {invoice?.status === BackendInvoiceStatus.Sent && invoice?.paymentLink && (
              <div className="mt-12 pt-12 border-t border-slate-100 text-center">
                <button
                  onClick={() => window.open(invoice.paymentLink!, '_blank')}
                  className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all font-bold"
                >
                  <CreditCard size={18} /> Pay Now (Stripe)
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
}